/**
 * Proactive engagement triggers for the Fyncall Widget
 * Handles page time, scroll depth, exit intent, page visit, cart value, and returning visitor triggers
 */

import { getState, getProactiveState, updateProactiveState, markTriggerShown, hasTriggerBeenShown, initProactiveState, subscribe } from '../core/state.js';
import { getConfig, getTenantConfig } from '../core/config.js';
import {
    trackVisit,
    getVisitCount,
    saveProactiveState as saveProactiveToStorage,
    loadProactiveState as loadProactiveFromStorage,
    removeSessionItem,
    STORAGE_KEYS
} from '../utils/storage.js';
import { throttle } from '../utils/helpers.js';

/**
 * Proactive trigger callbacks
 */
let triggerCallbacks = {
    onShowMessage: null,
    onOpenWidget: null,
    getCartValue: null,
    detectShopifyContext: null
};

/**
 * Active trigger timers and intervals
 */
const activeTimers = new Set();
const activeIntervals = new Set();

/**
 * Pending triggers that were skipped because widget was open
 * These will be retried when the widget closes
 */
const pendingTriggers = new Map(); // triggerId -> { trigger, proactiveConfig, tenantConfig }

/**
 * State subscription cleanup function
 */
let stateUnsubscribe = null;

/**
 * Local proactive state (for backwards compatibility with app.js)
 */
let localProactiveState = {
    triggersShown: {},
    triggerCount: 0,
    lastTriggerTime: 0,
    scrollDepth: 0,
    pageLoadTime: Date.now(),
    exitIntentBound: false,
    scrollListenerBound: false
};

/**
 * Initialize proactive state from storage
 */
export function loadProactiveSessionState() {
    let rawSessionValue = null;
    try {
        rawSessionValue = sessionStorage.getItem('fyncall_proactive_state');
    } catch (e) {
        console.warn('[FyncallProactive] Unable to read sessionStorage raw value:', e);
    }
    console.log('[FyncallProactive] raw sessionStorage entry before reset:', rawSessionValue);

    const saved = loadProactiveFromStorage();
    console.log('[FyncallProactive] parsed proactive state before reset:', saved);

    removeSessionItem(STORAGE_KEYS.PROACTIVE_STATE);
    if (saved) {
        localProactiveState.triggersShown = saved.triggersShown || {};
        localProactiveState.triggerCount = saved.triggerCount || 0;
        localProactiveState.lastTriggerTime = saved.lastTriggerTime || 0;
    }
    // Also init centralized state
    initProactiveState();
}

/**
 * Save proactive state to storage
 */
export function saveProactiveSessionState() {
    saveProactiveToStorage({
        triggersShown: localProactiveState.triggersShown,
        triggerCount: localProactiveState.triggerCount,
        lastTriggerTime: localProactiveState.lastTriggerTime
    });
}

/**
 * Get local proactive state
 * @returns {Object} Current proactive state
 */
export function getLocalProactiveState() {
    return { ...localProactiveState };
}

/**
 * Match URL against wildcard pattern
 * @param {string} pattern - Wildcard pattern (e.g., "*\/pricing*")
 * @param {string} url - URL to test (defaults to current URL)
 * @returns {boolean} True if URL matches pattern
 */
export function matchesUrlPattern(pattern, url = window.location.href) {
    if (!pattern) return false;

    // Convert wildcard pattern to regex
    // e.g., "*/pricing*" -> matches any URL containing "/pricing"
    const regexPattern = pattern
        .replace(/[.+^${}()|[\]\\]/g, '\\$&')  // Escape special chars except *
        .replace(/\*/g, '.*');  // Convert * to .*

    try {
        const regex = new RegExp(regexPattern, 'i');
        return regex.test(url);
    } catch (e) {
        console.warn('[FyncallProactive] Invalid URL pattern:', pattern);
        return false;
    }
}

/**
 * Check if current time is within business hours
 * @param {Object} businessHours - Business hours configuration
 * @returns {boolean} True if within business hours
 */
export function isWithinBusinessHours(businessHours) {
    if (!businessHours || !businessHours.enabled) return true;

    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = dayNames[now.getDay()];
    const todaySchedule = businessHours.schedule?.[today];

    if (!todaySchedule || !todaySchedule.enabled) {
        return false;
    }

    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [startHour, startMin] = (todaySchedule.start || '09:00').split(':').map(Number);
    const [endHour, endMin] = (todaySchedule.end || '17:00').split(':').map(Number);
    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;

    return currentTime >= startTime && currentTime <= endTime;
}

/**
 * Check if user is a returning visitor
 * @param {number} minVisitCount - Minimum visit count to qualify
 * @returns {boolean} True if returning visitor
 */
export function isReturningVisitor(minVisitCount = 2) {
    trackVisit();
    const visitCount = getVisitCount();
    console.log(`[FyncallProactive] Visit count: ${visitCount}, required: ${minVisitCount}`);
    return visitCount >= minVisitCount;
}

/**
 * Setup proactive engagement
 * @param {Object} callbacks - Trigger callbacks
 * @param {Function} callbacks.onShowMessage - Show proactive message
 * @param {Function} callbacks.onOpenWidget - Open the widget
 * @param {Function} callbacks.getCartValue - Get current cart value (for cart_value trigger)
 * @param {Function} callbacks.detectShopifyContext - Detect Shopify context for cart
 * @param {Object} config - Widget configuration
 * @param {Object} tenantConfig - Tenant configuration
 */
export function setupProactiveEngagement(callbacks, config = null, tenantConfig = null) {
    triggerCallbacks = { ...triggerCallbacks, ...callbacks };

    // Use passed config or get from centralized config
    const widgetConfig = config || getConfig();
    const tenant = tenantConfig || getTenantConfig();
    const proactiveConfig = tenant?.proactive;

    // Load session state from storage
    loadProactiveSessionState();

    // Fallback to legacy behavior if no server config
    if (!proactiveConfig || !proactiveConfig.enabled) {
        const proactiveEnabled = widgetConfig.proactiveEnabled || widgetConfig.enableProactive;
        if (proactiveEnabled) {
            const delay = widgetConfig.proactiveDelay || 30000;
            const timerId = setTimeout(() => {
                showProactiveMessage(widgetConfig.proactiveMessage);
            }, delay);
            activeTimers.add(timerId);
        }
        return;
    }

    console.log('[FyncallProactive] Setting up triggers with config:', proactiveConfig);

    const triggers = proactiveConfig.triggers || [];
    if (triggers.length === 0) {
        console.log('[FyncallProactive] No triggers configured');
        return;
    }

    // Setup each trigger
    triggers.forEach(trigger => {
        if (!trigger.enabled) return;
        setupTrigger(trigger, proactiveConfig, tenant);
    });
}

/**
 * Setup individual trigger
 * @param {Object} trigger - Trigger configuration
 * @param {Object} proactiveConfig - Proactive configuration
 * @param {Object} tenantConfig - Tenant configuration
 */
function setupTrigger(trigger, proactiveConfig, tenantConfig) {
    switch (trigger.type) {
        case 'page_time':
            setupPageTimeTrigger(trigger, proactiveConfig, tenantConfig);
            break;
        case 'scroll_depth':
            setupScrollDepthTrigger(trigger, proactiveConfig, tenantConfig);
            break;
        case 'exit_intent':
            setupExitIntentTrigger(trigger, proactiveConfig, tenantConfig);
            break;
        case 'page_visit':
            setupPageVisitTrigger(trigger, proactiveConfig, tenantConfig);
            break;
        case 'cart_value':
            setupCartValueTrigger(trigger, proactiveConfig, tenantConfig);
            break;
        case 'returning_visitor':
        case 'return_visitor':
            setupReturnVisitorTrigger(trigger, proactiveConfig, tenantConfig);
            break;
        default:
            console.warn('[FyncallProactive] Unknown trigger type:', trigger.type);
    }
}

/**
 * Setup page time trigger
 */
function setupPageTimeTrigger(trigger, proactiveConfig, tenantConfig) {
    const seconds = trigger.condition?.seconds || trigger.value || 30;
    const delay = (seconds * 1000) + (trigger.delay || 0);

    const timerId = setTimeout(() => {
        evaluateAndShowTrigger(trigger, proactiveConfig, tenantConfig);
    }, delay);

    activeTimers.add(timerId);
    console.log(`[FyncallProactive] Page time trigger set for ${seconds}s`);
}

/**
 * Setup scroll depth trigger
 */
function setupScrollDepthTrigger(trigger, proactiveConfig, tenantConfig) {
    if (localProactiveState.scrollListenerBound) return;
    localProactiveState.scrollListenerBound = true;

    const scrollHandler = throttle(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;

        localProactiveState.scrollDepth = Math.max(localProactiveState.scrollDepth, scrollPercent);

        // Check all scroll_depth triggers
        const triggers = proactiveConfig.triggers?.filter(t => t.type === 'scroll_depth' && t.enabled) || [];
        triggers.forEach(t => {
            const targetPercent = t.condition?.percentage || t.value || 50;
            if (localProactiveState.scrollDepth >= targetPercent) {
                evaluateAndShowTrigger(t, proactiveConfig, tenantConfig);
            }
        });
    }, 200);

    window.addEventListener('scroll', scrollHandler, { passive: true });
    console.log('[FyncallProactive] Scroll listener bound');
}

/**
 * Setup exit intent trigger
 */
function setupExitIntentTrigger(trigger, proactiveConfig, tenantConfig) {
    if (localProactiveState.exitIntentBound) return;
    localProactiveState.exitIntentBound = true;

    const exitHandler = (e) => {
        if (e.clientY <= 0) {
            const triggers = proactiveConfig.triggers?.filter(t => t.type === 'exit_intent' && t.enabled) || [];
            triggers.forEach(t => {
                evaluateAndShowTrigger(t, proactiveConfig, tenantConfig);
            });
        }
    };

    document.addEventListener('mouseleave', exitHandler);
    console.log('[FyncallProactive] Exit intent listener bound');
}

/**
 * Setup page visit trigger
 */
function setupPageVisitTrigger(trigger, proactiveConfig, tenantConfig) {
    const urlPattern = trigger.condition?.url_pattern;
    if (urlPattern && matchesUrlPattern(urlPattern)) {
        const timerId = setTimeout(() => {
            evaluateAndShowTrigger(trigger, proactiveConfig, tenantConfig);
        }, trigger.delay || 0);
        activeTimers.add(timerId);
    }
}

/**
 * Setup cart value trigger
 */
function setupCartValueTrigger(trigger, proactiveConfig, tenantConfig) {
    const targetValue = trigger.condition?.cart_value || trigger.value || 100;

    const checkCart = () => {
        // Try callback first, then fallback to Shopify detection
        let cartValue = 0;

        if (triggerCallbacks.getCartValue) {
            cartValue = triggerCallbacks.getCartValue();
        } else if (triggerCallbacks.detectShopifyContext) {
            const context = triggerCallbacks.detectShopifyContext();
            if (context?.cart?.total_price) {
                // Shopify cart total is in cents
                cartValue = context.cart.total_price / 100;
            }
        }

        if (cartValue >= targetValue) {
            evaluateAndShowTrigger(trigger, proactiveConfig, tenantConfig);
        }
    };

    // Check after delay and then every 5 seconds
    const timerId = setTimeout(checkCart, trigger.delay || 1000);
    activeTimers.add(timerId);

    const intervalId = setInterval(checkCart, 5000);
    activeIntervals.add(intervalId);

    console.log(`[FyncallProactive] Cart value monitoring set for $${targetValue}`);
}

/**
 * Setup return visitor trigger
 */
function setupReturnVisitorTrigger(trigger, proactiveConfig, tenantConfig) {
    const minVisits = trigger.condition?.visit_count || trigger.value || 2;

    if (isReturningVisitor(minVisits)) {
        const timerId = setTimeout(() => {
            evaluateAndShowTrigger(trigger, proactiveConfig, tenantConfig);
        }, trigger.delay || 2000);
        activeTimers.add(timerId);
    }
}

/**
 * Evaluate conditions and show trigger
 * @param {Object} trigger - Trigger configuration
 * @param {Object} proactiveConfig - Proactive configuration
 * @param {Object} tenantConfig - Tenant configuration
 * @returns {boolean} True if trigger was shown
 */
export function evaluateAndShowTrigger(trigger, proactiveConfig, tenantConfig = null) {
    const triggerId = trigger.id || `${trigger.type}_${Date.now()}`;

    // Check if already shown
    if (localProactiveState.triggersShown[triggerId]) {
        console.log(`[FyncallProactive] Trigger ${triggerId} already shown`);
        return false;
    }

    // Check show_once flag
    if (trigger.show_once && localProactiveState.triggersShown[triggerId]) {
        console.log(`[FyncallProactive] Trigger ${triggerId} already shown (show_once)`);
        return false;
    }

    // Check widget state
    const widgetState = getState();
    if (widgetState.isOpen) {
        // Queue the trigger to fire when widget closes
        if (!pendingTriggers.has(triggerId)) {
            pendingTriggers.set(triggerId, { trigger, proactiveConfig, tenantConfig });
            console.log(`[FyncallProactive] Widget is open, queuing trigger ${triggerId} for later`);
            ensureStateSubscription();
        }
        return false;
    }

    // Check max triggers per session
    const maxTriggers = proactiveConfig.max_triggers_per_session || 3;
    if (localProactiveState.triggerCount >= maxTriggers) {
        console.log(`[FyncallProactive] Max triggers reached (${maxTriggers})`);
        return false;
    }

    // Check cooldown between triggers
    const cooldown = (proactiveConfig.cooldown_between_triggers || proactiveConfig.trigger_cooldown || 60) * 1000;
    const timeSinceLastTrigger = Date.now() - localProactiveState.lastTriggerTime;
    if (localProactiveState.lastTriggerTime > 0 && timeSinceLastTrigger < cooldown) {
        console.log(`[FyncallProactive] Cooldown active, ${Math.round((cooldown - timeSinceLastTrigger) / 1000)}s remaining`);
        return false;
    }

    // Check business hours if required
    if (proactiveConfig.respect_business_hours) {
        const businessHours = tenantConfig?.business_hours || getTenantConfig()?.business_hours;
        if (businessHours && !isWithinBusinessHours(businessHours)) {
            console.log('[FyncallProactive] Outside business hours, skipping trigger');
            return false;
        }
    }

    // Check target pages
    if (trigger.target_pages && trigger.target_pages.length > 0) {
        const matchesAnyPage = trigger.target_pages.some(pattern => matchesUrlPattern(pattern));
        if (!matchesAnyPage) {
            console.log('[FyncallProactive] Current page does not match target pages');
            return false;
        }
    }

    // Check page URL conditions (from centralized state module format)
    if (trigger.conditions?.page_url_contains) {
        const url = window.location.href.toLowerCase();
        const contains = trigger.conditions.page_url_contains.toLowerCase();
        if (!url.includes(contains)) return false;
    }

    if (trigger.conditions?.page_url_excludes) {
        const url = window.location.href.toLowerCase();
        const excludes = trigger.conditions.page_url_excludes.toLowerCase();
        if (url.includes(excludes)) return false;
    }

    // Show the trigger
    const message = trigger.message || 'Hi there! Need help finding something?';
    showProactiveMessage(message, trigger);

    // Update state
    localProactiveState.triggersShown[triggerId] = true;
    localProactiveState.triggerCount++;
    localProactiveState.lastTriggerTime = Date.now();
    saveProactiveSessionState();

    // Also update centralized state
    markTriggerShown(triggerId);

    console.log(`[FyncallProactive] Trigger ${triggerId} (${trigger.type}) shown`);
    return true;
}

/**
 * Show proactive message
 * @param {string} message - Message to show
 * @param {Object} trigger - Optional trigger data
 */
function showProactiveMessage(message, trigger = null) {
    const defaultMessage = "👋 Need any help? Feel free to ask!";
    const displayMessage = message || defaultMessage;

    console.log('[FyncallProactive] Showing message:', displayMessage);

    if (triggerCallbacks.onShowMessage) {
        triggerCallbacks.onShowMessage(displayMessage, trigger);
    }
}

/**
 * Check cart value triggers manually
 * @param {number} cartValue - Current cart value
 */
export function checkCartValueTriggers(cartValue) {
    const triggers = window.__fyncallCartTriggers || [];

    triggers.forEach(({ trigger, triggerId, targetValue }) => {
        if (cartValue >= targetValue && !hasTriggerBeenShown(triggerId)) {
            const config = getConfig();
            const tenantConfig = getTenantConfig();
            evaluateAndShowTrigger(trigger, tenantConfig?.proactive || {}, tenantConfig);
        }
    });
}

/**
 * Ensure state subscription is active for widget close detection
 */
function ensureStateSubscription() {
    if (stateUnsubscribe) return; // Already subscribed

    stateUnsubscribe = subscribe((key, newValue, oldValue) => {
        // When widget closes (isOpen changes from true to false)
        if (key === 'isOpen' && oldValue === true && newValue === false) {
            console.log('[FyncallProactive] Widget closed, checking pending triggers');
            retryPendingTriggers();
        }
    });
    console.log('[FyncallProactive] Subscribed to state changes for widget close detection');
}

/**
 * Retry pending triggers that were queued while widget was open
 */
function retryPendingTriggers() {
    if (pendingTriggers.size === 0) return;

    console.log(`[FyncallProactive] Retrying ${pendingTriggers.size} pending trigger(s)`);

    // Copy and clear pending triggers to avoid issues during iteration
    const triggersToRetry = new Map(pendingTriggers);
    pendingTriggers.clear();

    // Small delay to ensure widget is fully closed
    const timerId = setTimeout(() => {
        for (const [triggerId, { trigger, proactiveConfig, tenantConfig }] of triggersToRetry) {
            console.log(`[FyncallProactive] Retrying trigger ${triggerId}`);
            evaluateAndShowTrigger(trigger, proactiveConfig, tenantConfig);
        }
    }, 100);
    activeTimers.add(timerId);
}

/**
 * Cleanup proactive engagement (timers, intervals, listeners)
 */
export function cleanupProactiveEngagement() {
    // Clear all timers
    activeTimers.forEach(timerId => clearTimeout(timerId));
    activeTimers.clear();

    // Clear all intervals
    activeIntervals.forEach(intervalId => clearInterval(intervalId));
    activeIntervals.clear();

    // Clear pending triggers
    pendingTriggers.clear();

    // Unsubscribe from state changes
    if (stateUnsubscribe) {
        stateUnsubscribe();
        stateUnsubscribe = null;
    }

    // Reset state flags (listeners are not removed as they're passive)
    localProactiveState.scrollListenerBound = false;
    localProactiveState.exitIntentBound = false;
}

/**
 * Get current proactive state
 * @returns {Object} Proactive state
 */
export function getProactiveEngagementState() {
    return { ...localProactiveState };
}

/**
 * Reset proactive state (for testing)
 */
export function resetProactiveState() {
    localProactiveState = {
        triggersShown: {},
        triggerCount: 0,
        lastTriggerTime: 0,
        scrollDepth: 0,
        pageLoadTime: Date.now(),
        exitIntentBound: false,
        scrollListenerBound: false
    };
    pendingTriggers.clear();
    updateProactiveState({
        triggersShown: {},
        triggerCount: 0,
        lastTriggerTime: 0
    });
}
